import React, { useEffect, useRef, useState, useCallback } from "react";
import { Send, Loader2, MessagesSquare, Paperclip, X, FileText } from "lucide-react";
import { toast } from "sonner";
import { Button } from "@/components/ui/button";
import { formatDateTime } from "@/components/portal/portalConstants";
import { fileHref } from "@/components/account/accountConstants";

const initials = (name) =>
  (name || "?")
    .split(" ")
    .map((p) => p[0])
    .slice(0, 2)
    .join("")
    .toUpperCase();

const isImage = (name = "") => /\.(png|jpe?g|webp|gif)$/i.test(name);

// Self-contained chat panel used by both the staff and customer process detail
// pages. Supports text + file attachments and marks the thread read on load.
export const MessageThread = ({ title = "Messages", currentUserId, listFn, sendFn, markReadFn, onRead }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [body, setBody] = useState("");
  const [file, setFile] = useState(null);
  const [sending, setSending] = useState(false);
  const endRef = useRef(null);
  const fileRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const data = await listFn();
      setMessages(Array.isArray(data) ? data : []);
      if (markReadFn) {
        try {
          await markReadFn();
          onRead && onRead();
        } catch (e) {
          /* ignore */
        }
      }
    } catch (e) {
      /* handled globally */
    } finally {
      setLoading(false);
    }
  }, [listFn, markReadFn, onRead]);

  useEffect(() => {
    load();
  }, [load]);

  // Live updates: new messages from the other side appear without reloading the page.
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    const t = setInterval(() => { if (!document.hidden) loadRef.current(); }, 15000);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    endRef.current?.scrollIntoView({ behavior: "smooth", block: "end" });
  }, [messages.length]);

  const handleSend = async () => {
    const text = body.trim();
    if (!text && !file) return;
    setSending(true);
    try {
      const msg = await sendFn(text, file);
      setMessages((prev) => [...prev, msg]);
      setBody("");
      setFile(null);
      if (fileRef.current) fileRef.current.value = "";
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not send your message. Please try again.");
    } finally {
      setSending(false);
    }
  };

  return (
    <section
      className="rounded-2xl border border-white/10 bg-card/60 p-5"
      data-testid="message-thread"
    >
      <h2 className="font-semibold mb-4 flex items-center gap-2 text-slate-100">
        <MessagesSquare className="h-4 w-4 text-venka-green-light" /> {title}
      </h2>

      <div className="max-h-80 overflow-y-auto pr-1 space-y-3" data-testid="message-list">
        {loading && (
          <div className="grid place-items-center py-8 text-slate-500">
            <Loader2 className="h-5 w-5 animate-spin" />
          </div>
        )}
        {!loading && messages.length === 0 && (
          <p className="text-sm text-slate-400 py-6 text-center" data-testid="message-empty">
            No messages yet. Start the conversation.
          </p>
        )}
        {messages.map((m) => {
          const mine = m.sender_id === currentUserId;
          const href = fileHref(m.attachment_url);
          return (
            <div
              key={m.id}
              className={`flex gap-2.5 ${mine ? "flex-row-reverse" : ""}`}
              data-testid={`message-${m.id}`}
            >
              <span
                className={`flex h-8 w-8 shrink-0 items-center justify-center rounded-full text-[11px] font-semibold text-white ${
                  mine ? "brand-gradient" : "bg-slate-600"
                }`}
              >
                {initials(m.sender_name)}
              </span>
              <div className={`max-w-[78%] ${mine ? "text-right" : ""}`}>
                <div
                  className={`inline-block rounded-2xl px-3.5 py-2 text-sm text-left ${
                    mine
                      ? "bg-venka-blue/25 text-slate-50 rounded-tr-sm"
                      : "bg-slate-800 text-slate-100 rounded-tl-sm"
                  }`}
                >
                  {m.body}
                  {href && (
                    <a
                      href={href}
                      target="_blank"
                      rel="noreferrer"
                      className="mt-2 block"
                      data-testid={`message-attachment-${m.id}`}
                    >
                      {isImage(m.attachment_name) ? (
                        <img src={href} alt={m.attachment_name} className="max-h-40 rounded-lg border border-white/10" />
                      ) : (
                        <span className="inline-flex items-center gap-1.5 rounded-lg bg-black/20 px-2.5 py-1.5 text-xs text-venka-green-light">
                          <FileText className="h-3.5 w-3.5" /> {m.attachment_name || "Attachment"}
                        </span>
                      )}
                    </a>
                  )}
                </div>
                <p className="text-[10.5px] text-slate-500 mt-1 px-1">
                  {m.sender_name}
                  {m.sender_role && m.sender_role !== "customer" ? " · Agent" : ""} ·{" "}
                  {formatDateTime(m.created_at)}
                </p>
              </div>
            </div>
          );
        })}
        <div ref={endRef} />
      </div>

      {file && (
        <div className="mt-3 flex items-center justify-between gap-2 rounded-lg bg-slate-950/60 border border-white/10 px-3 py-2 text-xs text-slate-300" data-testid="message-file-chip">
          <span className="inline-flex items-center gap-1.5 truncate"><Paperclip className="h-3.5 w-3.5 text-venka-green-light" /> {file.name}</span>
          <button onClick={() => { setFile(null); if (fileRef.current) fileRef.current.value = ""; }} className="text-slate-400 hover:text-white"><X className="h-3.5 w-3.5" /></button>
        </div>
      )}

      <div className="mt-4 flex items-end gap-2">
        <input
          ref={fileRef}
          type="file"
          accept=".pdf,.png,.jpg,.jpeg"
          className="hidden"
          onChange={(e) => setFile(e.target.files?.[0] || null)}
          data-testid="message-file-input"
        />
        <button
          type="button"
          onClick={() => fileRef.current?.click()}
          className="flex h-10 w-10 items-center justify-center rounded-xl border border-white/15 bg-slate-950/60 text-slate-300 hover:text-white hover:border-white/30 transition-colors shrink-0"
          title="Attach a file"
          data-testid="message-attach-button"
        >
          <Paperclip className="h-4 w-4" />
        </button>
        <textarea
          value={body}
          onChange={(e) => setBody(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          rows={1}
          placeholder="Write a message…"
          className="flex-1 resize-none rounded-xl border border-white/15 bg-slate-950/60 px-3 py-2.5 text-sm text-slate-100 placeholder:text-slate-500 focus:outline-none focus:ring-2 focus:ring-venka-blue/40"
          data-testid="message-input"
        />
        <Button
          onClick={handleSend}
          disabled={sending || (!body.trim() && !file)}
          className="rounded-xl brand-gradient text-white hover:opacity-90 shrink-0"
          data-testid="message-send-button"
        >
          {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
        </Button>
      </div>
    </section>
  );
};

export default MessageThread;

